// SMSF Calculator - Mobile Interface
// Dual approach: SMSF Lite (15 tools, mobile-optimized) + SMSF Full (36 tools, desktop in mobile)
// Full SMSF suite (36 tools) available on desktop

import React, { useState, useRef } from 'react';
import { SimulationEngine } from '../SimulationEngine';
import { FinancialConfig } from '../FinancialConfig';
import SMSFLiteMobile from './SMSFLiteMobile';
import { EmailCaptureForm } from './EmailCaptureForm';

export const SMSFCalculatorMobile = ({ onBack }) => {
    const [mode, setMode] = useState('choice'); // 'choice', 'lite', 'full'
    const [showDesktopPrompt, setShowDesktopPrompt] = useState(true);
    
    // Choice Screen - Select between Lite and Full
    if (mode === 'choice') {
        return (
            <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '20px' }}>
                {/* Header */}
                <div className="bg-sky-600 text-white px-3 py-3" 
                     style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                    <div className="flex items-center justify-between">
                        <button onClick={onBack} className="p-1 active:bg-sky-700 rounded">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h1 className="text-base font-bold">SMSF Calculator</h1>
                        <div className="w-7"></div>
                    </div>
                </div>
                
                {/* Choice Screen Content */}
                <div className="p-4 space-y-4">
                    <div className="bg-white rounded-2xl p-6 shadow-lg mb-4">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Choose Your Experience</h2>
                        <p className="text-sm text-gray-600 text-center">Select the version that best suits your needs</p>
                    </div>
                    
                    {/* SMSF Lite Option */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-1 rounded-2xl shadow-xl">
                        <div className="bg-white rounded-xl p-6">
                            <div className="flex items-start gap-4 mb-4">
                                <div className="text-4xl">✨</div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                                        SMSF Lite
                                    </h3>
                                    <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                                        <strong>Top 15 essential tools</strong> optimized for mobile. Perfect for most SMSF needs with a streamlined, mobile-friendly interface.
                                    </p>
                                    <div className="bg-green-50 rounded-xl p-3 mb-3">
                                        <h4 className="font-bold text-green-900 mb-2 text-xs">Includes:</h4>
                                        <div className="grid grid-cols-1 gap-1 text-xs text-green-800">
                                            <div>✓ Contribution Tracking & Caps</div>
                                            <div>✓ Pension Optimizer</div>
                                            <div>✓ LRBA Calculator</div>
                                            <div>✓ Division 293 Tax</div>
                                            <div>✓ Cash Flow Modeling</div>
                                            <div>✓ Investment Strategy</div>
                                            <div>✓ TBC Tracking</div>
                                            <div>✓ Fee Analysis</div>
                                            <div>✓ Compliance Basics</div>
                                            <div className="text-green-600 italic">...and 6 more</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-green-600 text-xs mb-4">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-semibold">Recommended for Mobile</span>
                                    </div>
                                    <button
                                        onClick={() => setMode('lite')}
                                        className="w-full py-4 bg-green-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-green-700"
                                    >
                                        Use SMSF Lite
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Email capture - SMSF insights */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                        <EmailCaptureForm calculatorType="smsf" results={null} inputs={null} getPdfBase64={null} />
                    </div>
                    
                    {/* SMSF Full Option */}
                    <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white p-1 rounded-2xl shadow-xl">
                        <div className="bg-white rounded-xl p-6">
                            <div className="flex items-start gap-4 mb-4">
                                <div className="text-4xl">💼</div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                                        SMSF Full Suite
                                    </h3>
                                    <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                                        <strong>All 36 professional tools</strong> for comprehensive SMSF management. Desktop interface adapted for mobile.
                                    </p>
                                    <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-3 mb-3">
                                        <div className="flex items-start gap-2">
                                            <span className="text-lg">⚠️</span>
                                            <div className="flex-1">
                                                <h4 className="font-bold text-yellow-900 mb-1 text-xs">UI Not Mobile-Optimized</h4>
                                                <p className="text-xs text-yellow-800 leading-relaxed">
                                                    This is the full desktop experience in your mobile browser. Interface may be crowded on small screens. 
                                                    Best used on tablet or large phones in landscape mode.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-sky-50 rounded-xl p-3 mb-3">
                                        <h4 className="font-bold text-sky-900 mb-2 text-xs">All 36 Tools:</h4>
                                        <div className="grid grid-cols-2 gap-1 text-xs text-sky-800">
                                            <div>• Contribution Caps</div>
                                            <div>• Carry Forward</div>
                                            <div>• Pension Optimizer</div>
                                            <div>• LRBA Calculator</div>
                                            <div>• Division 293</div>
                                            <div>• Cash Flow</div>
                                            <div>• Insurance</div>
                                            <div>• Estate Planning</div>
                                            <div>• Tax Harvesting</div>
                                            <div>• Collectibles</div>
                                            <div>• Related Party</div>
                                            <div>• Compliance</div>
                                            <div className="col-span-2 text-sky-600 italic text-center">...and 24 more</div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setMode('full')}
                                        className="w-full py-4 bg-sky-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-sky-700"
                                    >
                                        Use Full Suite (Advanced)
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Desktop Recommendation */}
                    <div className="bg-gray-100 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                            <span className="text-2xl">🖥️</span>
                            <div>
                                <h4 className="font-bold text-gray-900 mb-1 text-sm">Desktop Version</h4>
                                <p className="text-xs text-gray-700 mb-3 leading-relaxed">
                                    For the best experience with all 36 tools and full-sized interface, use a desktop or laptop computer.
                                </p>
                                <a
                                    href="/app.html"
                                    className="inline-block px-4 py-2 bg-gray-700 text-white font-semibold text-xs rounded-lg active:bg-gray-800"
                                >
                                    Open Desktop Version →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    // SMSF Lite Mode - Full implementation
    if (mode === 'lite') {
        return <SMSFLiteMobile onBack={() => setMode('choice')} />;
    }
    
    // SMSF Full Mode
    if (mode === 'full') {
        return (
            <SMSFFullMobile onBack={() => setMode('choice')} />
        );
    }
    
    // Fallback UI (should never reach here - all calculators are implemented)
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '20px' }}>
            {/* Header */}
            <div className="bg-sky-600 text-white px-3 py-3" 
                 style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-sky-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">SMSF Calculator</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4">
                {/* Desktop Recommendation */}
                {showDesktopPrompt && (
                    <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white p-1 rounded-2xl shadow-xl">
                        <div className="bg-white rounded-xl p-6">
                            <div className="flex items-start gap-4 mb-4">
                                <div className="text-4xl">💼</div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                                        Full SMSF Suite
                                    </h2>
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        The SMSF Calculator Suite includes <strong>36 specialized tools</strong> for comprehensive self-managed super fund management.
                                    </p>
                                </div>
                            </div>
                            
                            {/* Feature List */}
                            <div className="bg-sky-50 rounded-xl p-4 mb-4">
                                <h3 className="font-bold text-sky-900 mb-3 text-sm">Included Tools:</h3>
                                <div className="grid grid-cols-2 gap-2 text-xs text-sky-800">
                                    <div>• LRBA Calculator</div>
                                    <div>• Division 293 Tax</div>
                                    <div>• Pension Optimizer</div>
                                    <div>• Cash Flow Modeling</div>
                                    <div>• Insurance Analysis</div>
                                    <div>• Tax Loss Harvesting</div>
                                    <div>• Bring-Forward Rules</div>
                                    <div>• FHSS Calculator</div>
                                    <div>• Investment Strategy</div>
                                    <div>• Compliance Monitor</div>
                                    <div>• Contribution Caps</div>
                                    <div>• TBC Tracking</div>
                                </div>
                            </div>
                            
                            {/* Recommendation */}
                            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-4">
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">💡</span>
                                    <div>
                                        <h4 className="font-bold text-yellow-900 mb-1 text-sm">Best Experience on Desktop</h4>
                                        <p className="text-xs text-yellow-800 leading-relaxed">
                                            Due to the complexity of SMSF management and the large number of interactive tools, 
                                            we recommend using the desktop version for the full experience.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="space-y-3">
                                <a
                                    href="/app.html"
                                    className="block w-full py-4 bg-sky-600 text-white font-bold text-base rounded-xl shadow-lg text-center active:bg-sky-700"
                                >
                                    🖥️ Open Desktop Version
                                </a>
                                <button
                                    onClick={() => setShowDesktopPrompt(false)}
                                    className="w-full py-3 bg-white text-gray-700 font-semibold text-sm rounded-xl border-2 border-gray-300"
                                >
                                    Continue on Mobile (Limited Features)
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Mobile Version (if user dismisses prompt) */}
                {!showDesktopPrompt && (
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            Basic SMSF Projection
                        </h2>
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                            <p className="text-sm text-blue-800">
                                📱 <strong>Mobile Version:</strong> Provides basic retirement projection using SMSF balance.
                            </p>
                            <p className="text-xs text-blue-700 mt-2">
                                For full SMSF tools (compliance, tax planning, contribution caps, etc.), please use desktop.
                            </p>
                        </div>
                        
                        {/* Simple Input Form */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    SMSF Total Balance ($)
                                </label>
                                <input
                                    type="number"
                                    placeholder="500000"
                                    className="w-full px-4 py-4 text-xl font-bold text-right border-2 border-gray-300 rounded-xl"
                                    style={{ fontSize: '18px' }}
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Your Age
                                </label>
                                <input
                                    type="number"
                                    placeholder="67"
                                    className="w-full px-4 py-4 text-xl font-bold text-right border-2 border-gray-300 rounded-xl"
                                    style={{ fontSize: '18px' }}
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Retirement Period (years)
                                </label>
                                <input
                                    type="number"
                                    placeholder="20"
                                    className="w-full px-4 py-4 text-xl font-bold text-right border-2 border-gray-300 rounded-xl"
                                    style={{ fontSize: '18px' }}
                                />
                            </div>
                            
                            <button className="w-full py-4 bg-sky-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-sky-700">
                                Calculate Basic Projection
                            </button>
                        </div>
                        
                        {/* Link to Desktop */}
                        <div className="mt-6 text-center">
                            <a
                                href="/app.html"
                                className="text-sky-600 font-semibold text-sm underline"
                            >
                                → Switch to Desktop for Full SMSF Suite
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// SMSF Full - All 36 tools, desktop interface in mobile viewport
const SMSFFullMobile = ({ onBack }) => {
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '20px' }}>
            <div className="bg-sky-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-sky-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">SMSF Full Suite</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4">
                {/* Warning Banner */}
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                        <span className="text-2xl">⚠️</span>
                        <div>
                            <h4 className="font-bold text-yellow-900 mb-1 text-sm">Desktop Interface</h4>
                            <p className="text-xs text-yellow-800 leading-relaxed">
                                You're viewing the full desktop SMSF suite on mobile. Interface may be crowded. For best experience, rotate to landscape mode or use a larger device.
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Full Desktop Suite</h2>
                    <p className="text-gray-700 mb-4">
                        The full SMSF suite with all 36 tools is best accessed through the desktop version where the interface has full space to display all features properly.
                    </p>
                    <p className="text-sm text-gray-600 mb-6">
                        Mobile support for the full suite is available, but the desktop version provides the optimal experience.
                    </p>
                    <a
                        href="/app.html"
                        className="block w-full py-4 bg-sky-600 text-white font-bold text-base rounded-xl shadow-lg text-center active:bg-sky-700"
                    >
                        Open Desktop Version
                    </a>
                </div>
            </div>
        </div>
    );
};

export default SMSFCalculatorMobile;

